import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DataTable,
  type SortDirection,
  type SortState,
} from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { InviteUserDialog } from '@/components/ui/invite-user-dialog'
import { useTeamMembers } from '@/hooks/useTeamMembers'
import { getTeamActions, teamColumns } from '../team-table-config'

export function TeamSection() {
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  })
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Get team members data and actions
  const {
    teamMembers,
    isLoading,
    hasGodModePermissions,
    removeUser,
    revokeInvitation,
    resendInvitation,
    refetch,
  } = useTeamMembers()

  const handleSort = (column: string, direction: SortDirection) => {
    setSortState({ column, direction })
  }

  // Filter team members based on search query
  const filteredTeamMembers = teamMembers.filter((member) => {
    if (!searchQuery) return true

    const searchLower = searchQuery.toLowerCase()
    if (member.type === 'user') {
      return (
        member.fullName.toLowerCase().includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower)
      )
    } else {
      return member.email.toLowerCase().includes(searchLower)
    }
  })

  const handleInviteSuccess = () => {
    refetch()
  }

  return (
    <div className="space-y-6">
      {/* Search Bar and Invite Button */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-9 rounded-full border-border shadow-[0px_2px_2px_-1px_rgba(83,115,162,0.05)] text-sm text-muted-foreground"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
        <Button
          variant="outline"
          className="h-9 rounded-full flex items-center gap-2"
          onClick={() => setIsInviteDialogOpen(true)}
        >
          <Plus className="h-4 w-4" />
          Invite User
        </Button>
      </div>

      {/* Team Members Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-sm text-muted-foreground">
            Loading team members...
          </div>
        </div>
      ) : (
        <DataTable
          data={filteredTeamMembers}
          columns={teamColumns}
          getActions={(item) =>
            getTeamActions(
              item,
              hasGodModePermissions,
              async (id: string) => {
                await removeUser(id)
              },
              async (id: string) => {
                await revokeInvitation(id)
              },
              async (id: string) => {
                await resendInvitation(id)
              }
            )
          }
          getItemId={(member) => member.id}
          emptyMessage={
            searchQuery
              ? 'No team members match your search.'
              : 'No team members found.'
          }
          stickyHeader={true}
          sortState={sortState}
          onSort={handleSort}
          maxHeight=""
          showHeader={false}
        />
      )}

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={isInviteDialogOpen}
        onOpenChange={setIsInviteDialogOpen}
        onInviteSent={handleInviteSuccess}
        teamMembers={teamMembers}
      />
    </div>
  )
}
